import { errorMessage } from "@atlas/shared";
import { useMutation } from "@tanstack/react-query";
import { Check, CloudOff } from "lucide-react";
import { Button } from "./Button";
import { submitFeedback } from "../api/feedback";

// Our Strava partnership caps how many athletes can connect, and the cap
// fills up. Strava blocks the OAuth authorize page itself with a dead-end 403
// ("Limit of connected athletes exceeded") - no callback ever fires, so the
// only place we can act is before the click. The gate is the server-side
// feature flag `strava_connections_full` (admin > Flags), read at runtime via
// useStravaConnectionsFull() so flipping it needs no redeploy. While it is on:
//  - Login/Signup hide the "connect with Strava" option entirely
//  - Connected sources swaps the connect button for this notice + waitlist
//  - The API refuses new Strava authorisations (backstop for stale clients)
// Waitlist requests land in the feedback inbox (details.request =
// "strava-waitlist") with the submitter's account email attached, so we know
// who to notify, in order, when capacity opens.

// Invite bypass: when a seat is freed for one waitlisted person, their notify
// email links to /connected-sources?strava=invited. Seeing that param stores a
// flag so the bypass survives a login redirect, and Connected sources then
// shows the real connect button instead of the capacity notice, linking to
// /auth/strava?invite=1 so the API-side guard is skipped too. This is soft -
// the seat itself is whatever headroom exists on Strava's side, so an
// uninvited person who guesses the URL could take it, which we accept for a
// hand-managed waitlist of this size.
const INVITE_STORAGE_KEY = "atlas-strava-invite";

export function hasStravaInviteBypass(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get("strava") === "invited") {
      localStorage.setItem(INVITE_STORAGE_KEY, "1");
      return true;
    }
    return localStorage.getItem(INVITE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

// Shown on the Strava card in Connected sources in place of the connect
// button. Already-connected users are unaffected.
export function StravaCapacityNotice() {
  const waitlistMutation = useMutation({
    mutationFn: () =>
      submitFeedback({
        type: "other",
        title: "Strava capacity waitlist",
        body: "Asked to be notified when Strava connection capacity opens up.",
        details: { request: "strava-waitlist" },
        context: {
          url: window.location.href,
          userAgent: navigator.userAgent,
        },
      }),
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: "10px 12px",
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-md)",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <CloudOff size={14} style={{ color: "var(--text-muted)", flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.5 }}>
          <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
            Strava connections are temporarily full.
          </span>{" "}
          We're on Strava's partner programme, which caps how many athletes can connect
          while we grow. We've asked Strava for more capacity - in the meantime you can
          import activities by GPX below.
        </div>
      </div>
      {waitlistMutation.isSuccess ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12.5,
            fontWeight: 600,
            color: "var(--text-accent)",
          }}
        >
          <Check size={13} style={{ flexShrink: 0 }} />
          You're on the list - we'll email you when a spot opens.
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <Button
            variant="secondary"
            size="sm"
            disabled={waitlistMutation.isPending}
            onClick={() => waitlistMutation.mutate()}
          >
            {waitlistMutation.isPending ? "Adding you…" : "Notify me when a spot opens"}
          </Button>
          {waitlistMutation.isError && (
            <span style={{ fontSize: 12, color: "var(--status-danger)" }}>
              {errorMessage(waitlistMutation.error, "Something went wrong - try again.")}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
